import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomText from "pages/_components/CustomText/CustomText";
import FormattedAmount from "pages/_components/FormattedAmount";
import GridTable from "pages/_components/GridTable/GridTable";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import * as i18n from "util/i18n";
import Notification from "pages/_components/Notification";
import CustomData from "pages/_components/CustomData/CustomData";
import { goBack, push } from "connected-react-router";
import { actions as discountsActions } from "reducers/discounts";
import Image from "pages/_components/Image";
import gaEventTracker, { events } from "util/gaEventTracker";
import { bool } from "prop-types";
import classNames from "classnames";
import getCellPlacement from "../../../util/helpers/table";

const DiscountSimulationPage = ({ isDesktop }) => {
    const [statusChecked, setStatusChecked] = useState(false);
    const { discountSimulation, discountStatus } = useSelector((_state) => _state.discounts);
    const { echeqs: simulationEcheqs, totalAmount: simulationAmount } = discountSimulation;
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(null);
    const getCellProps = (row, col, cols) => getCellPlacement(row, col, { isDesktop, totalColumns: cols });
    const handleAccordion = (i) => {
        if (isOpen === i) {
            return setIsOpen(null);
        }

        return setIsOpen(i);
    };

    useEffect(() => {
        if (!statusChecked) {
            if (discountStatus !== "SIMULATING_DISCOUNT") {
                dispatch(push("/discounts"));
            }
            setStatusChecked(true);
        } else if (discountStatus === "CREATING_DISCOUNT") {
            dispatch(push("/discounts"));
        } else if (discountStatus === "REVIEW_DISCOUNT") {
            dispatch(push("/discounts/review"));
        }
    }, [discountStatus, statusChecked]);

    useEffect(
        () => () => {
            dispatch(discountsActions.restartDiscountStatus("REVIEW_DISCOUNT", "CREATING_DISCOUNT"));
        },
        [],
    );

    const onBack = () => {
        dispatch(discountsActions.setDiscountStatus("CREATING_DISCOUNT"));
        dispatch(goBack());
    };

    const submit = () => {
        let sumaPlazos = 0;
        const echeqsFormatted = simulationEcheqs.reduce((final, echeq) => {
            const difference = new Date(echeq.fecha_pago).getTime() - new Date(echeq.fecha_emision).getTime();
            const plazo = Math.ceil(difference / (1000 * 3600 * 24));
            sumaPlazos += plazo;

            return [
                ...final,
                {
                    id_cheque: echeq.cheque_id,
                    monto: echeq.discountEcheqExtractData?.amount?.quantity,
                    montoAcreditar: echeq.discountEcheqExtractData?.effectiveAmount?.quantity,
                },
            ];
        }, []);

        gaEventTracker.triggerEvent(
            events.echeq_confirmar({
                datetime: new Date(),
                echeqs: echeqsFormatted,
                plazoPromedio: simulationEcheqs?.length ? sumaPlazos / simulationEcheqs?.length : null,
                Id_lote: discountSimulation?.idBatch,
            }),
        );

        dispatch(discountsActions.setDiscountStatus("REVIEW_DISCOUNT"));
    };

    const deleteZeroDecimals = (num) => (num - Math.floor(num) > 0 ? num : Math.floor(num));

    const getPercentage = (num) => `${deleteZeroDecimals(Number(num)) || 0}%`;

    return (
        <>
            <Notification scopeToShow="DiscountSimulationPage" />
            <ResponsiveHeader defaultTitle="Descuento de Echeqs" onClickBack={onBack} />
            <MainContainer className="discounts-echeq-simulation">
                <div className="container pb-8">
                    <div className="discounts-echeq-simulation__available-discounts">
                        <CustomText text="Monto total" />
                        <FormattedAmount
                            quantity={simulationAmount?.quantity || 0}
                            currency={simulationAmount?.currency || "ARS"}
                            className="echeq-table__available-amount font-weight-700"
                        />
                    </div>

                    <ul className="discounts-echeq-simulation__list">
                        {simulationEcheqs?.map((echeq, i) => (
                            <li key={echeq.cheque_id}>
                                <button type="button" onClick={() => handleAccordion(i)}>
                                    <GridTable
                                        className={classNames("discounts-echeq-simulation__list-header", {
                                            "pb-6": isOpen === i,
                                        })}>
                                        <GridTable.Container
                                            columnsTemplate={isDesktop ? "1.3fr 1fr 1fr 1fr 2rem" : "1fr"}>
                                            <GridTable.Data {...getCellProps(1, 1)}>
                                                <CustomData
                                                    titleText={i18n.get("", "Nº Echeq")}
                                                    titleClass="text-Primary-primary-color"
                                                    valueText={echeq.cheque_numero}
                                                    dataClass="discounts-echeq-simulation__cheq-number"
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data {...getCellProps(1, 2)}>
                                                <CustomData
                                                    titleText={i18n.get("", "Monto a negociar")}
                                                    valueText={echeq.discountEcheqExtractData?.amount?.quantity}
                                                    valueTextType={echeq.emisor_moneda}
                                                    dataClass="discounts-echeq-simulation__emisor"
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data {...getCellProps(1, 3)}>
                                                <CustomData
                                                    titleText={i18n.get("", "Monto a acreditar *")}
                                                    valueText={
                                                        echeq.discountEcheqExtractData?.effectiveAmount?.quantity
                                                    }
                                                    valueTextType={echeq.emisor_moneda}
                                                    dataClass="discounts-echeq-simulation__emisor"
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data {...getCellProps(1, 4)}>
                                                <CustomData
                                                    titleText={i18n.get("", "Tasa *")}
                                                    valueText={getPercentage(echeq.discountEcheqExtractData?.tax?.rate)}
                                                    dataClass="discounts-echeq-simulation__tax-rate"
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data
                                                columnStart={isDesktop ? 5 : 2}
                                                rowStart={1}
                                                alignX="flex-end"
                                                alignY={isDesktop ? "center" : "start"}>
                                                <div
                                                    className={classNames(
                                                        "discounts-echeq-simulation__arrow svg-wrapper",
                                                        {
                                                            "arrow-rotate": isOpen === i,
                                                        },
                                                    )}>
                                                    <Image src="images/chevromRight.svg" className="svg-icon" />
                                                </div>
                                            </GridTable.Data>
                                        </GridTable.Container>
                                    </GridTable>
                                    <hr
                                        className={`dotted discounts-echeq-simulation__line ${
                                            isOpen === i
                                                ? "discounts-echeq-simulation__open"
                                                : "discounts-echeq-simulation__close"
                                        }`}
                                    />
                                    <GridTable
                                        className={`discounts-echeq-simulation__list-details ${
                                            isOpen === i
                                                ? "discounts-echeq-simulation__open"
                                                : "discounts-echeq-simulation__close"
                                        }`}>
                                        <GridTable.Container
                                            columnsTemplate={isDesktop ? "1.3fr 1fr 1fr 1fr 2rem" : "1fr"}>
                                            <GridTable.Data {...getCellProps(1, 1, 4)}>
                                                <CustomData
                                                    titleText={i18n.get("detail.discounts.recibedTo", "Recibido de")}
                                                    titleClass="text-Primary-primary-color"
                                                    valueText={
                                                        <>
                                                            <span>{`CUIT ${echeq.emisor_cuit}`}</span> 
                                                            <br />
                                                            <span>{echeq.emisor_razon_social}</span>
                                                        </>
                                                    }
                                                    dataClass="discounts-echeq-simulation__emisor-cuit"
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data {...getCellProps(1, 2, 4)}>
                                                <CustomData
                                                    titleText={i18n.get("", "Fecha de emisión")}
                                                    valueText={echeq.fecha_emision}
                                                    valueTextType="DATE"
                                                    dataClass="discounts-echeq-simulation__date-emission"
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data {...getCellProps(1, 3, 4)}>
                                                <CustomData
                                                    titleText={i18n.get("", "Fecha de pago")}
                                                    valueText={echeq.fecha_pago}
                                                    valueTextType="DATE"
                                                    dataClass="discounts-echeq-simulation__date-pay"
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data {...getCellProps(1, 4, 4)}>
                                                <CustomData
                                                    titleText={i18n.get("", "Plazo")}
                                                    valueText={echeq.discountEcheqExtractData?.tax?.timeLimit}
                                                    dataClass="discounts-echeq-simulation__time-limit"
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data {...getCellProps(2, 1, 4)}>
                                                <CustomData
                                                    titleText={i18n.get("", "Intereses")}
                                                    valueText={echeq.discountEcheqExtractData?.tax?.interest}
                                                    valueTextType={echeq.emisor_moneda}
                                                    dataClass="discounts-echeq-simulation__tax-interest"
                                                />
                                            </GridTable.Data>
                                            {/* <GridTable.Data {...getCellProps(2, 2, 3)}>
                                                <CustomData
                                                    titleText={i18n.get("", "IVA")}
                                                    valueText={echeq.discountEcheqExtractData?.tax?.taxVat}
                                                    valueTextType={echeq.emisor_moneda}
                                                    dataClass="discounts-echeq-simulation__tax-iva"
                                                />
                                            </GridTable.Data> */}
                                            <GridTable.Data {...getCellProps(2, 2, 4)}>
                                                <CustomData
                                                    titleText={i18n.get("", "Comisión")}
                                                    valueText={echeq.discountEcheqExtractData?.tax?.commission}
                                                    valueTextType={echeq.emisor_moneda}
                                                    dataClass="discounts-echeq-simulation__tax-comission"
                                                />
                                            </GridTable.Data>
                                            {/*  <GridTable.Data {...getCellProps(3, 1, 3)}>
                                                <CustomData
                                                    titleText={i18n.get("", "Sellado")}
                                                    valueText={echeq.discountEcheqExtractData?.tax?.taxStamp}
                                                    valueTextType={echeq.emisor_moneda}
                                                    dataClass="discounts-echeq-simulation__tax-stamp"
                                                />
                                            </GridTable.Data>
                                            {discountSimulation.iibb && discountSimulation.iibb.length !== 0 ? (
                                                discountSimulation.iibb.map((iibbDiscount, index) => (
                                                    <>
                                                        <GridTable.Data {...getCellProps(3 + index, 2, 3)}>
                                                            {index === 0 ? (
                                                                <CustomData
                                                                    titleText={i18n.get("detail.echeq.IIBB", "IIBB")}
                                                                    valueText={String(iibbDiscount.importe)}
                                                                    valueTextType={echeq.emisor_moneda}
                                                                    dataClass="discounts-echeq-simulation__tax-iibb"
                                                                />
                                                            ) : (
                                                                <CustomText
                                                                    text={String(iibbDiscount.importe)}
                                                                    className="font-bold"
                                                                    textType={echeq.emisor_moneda}
                                                                />
                                                            )}
                                                        </GridTable.Data>
                                                        <GridTable.Data {...getCellProps(3 + index, 3, 3)}>
                                                            {index === 0 ? (
                                                                <CustomData
                                                                    titleText={i18n.get(
                                                                        "detail.echeq.jurisdiction",
                                                                        "Jurisdicción",
                                                                    )}
                                                                    valueText={String(iibbDiscount.jurisdiccion)}
                                                                    dataClass="discounts-echeq-simulation__tax-iibb"
                                                                />
                                                            ) : (
                                                                <CustomText
                                                                    text={String(iibbDiscount.jurisdiccion)}
                                                                    className="font-bold"
                                                                />
                                                            )}
                                                        </GridTable.Data>
                                                    </>
                                                ))
                                            ) : (
                                                <></>
                                            )} */}
                                        </GridTable.Container>
                                    </GridTable>
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className="discounts-echeq-simulation__rate">
                        <CustomText
                            text={`* ${i18n.get(
                                "discounts.simulation.taxRateDescription",
                                "Tasa Nominal Anual Adelantada.",
                            )}`}
                        />
                    </div>
                    <div className="discounts-echeq-simulation__rate">
                        <CustomText text={`* ${i18n.get("discounts.amountToCredit.echeq.disclaimer")}`} />
                    </div>
                    <GridTable className="grid-table-buttons">
                        <GridTable.Container>
                            <GridTable.Data columnStart={7} columnWidth={4}>
                                <CustomButton
                                    text={i18n.get("administration.advanced.group.create.button.confirm.text")}
                                    func={submit}
                                    className="btn-primary flex"
                                    loading={false}
                                />
                            </GridTable.Data>
                            <GridTable.Data columnStart={3} columnWidth={4}>
                                <CustomButton
                                    text={i18n.get("", "Volver a simular")}
                                    func={onBack}
                                    className="btn-outline flex"
                                />
                            </GridTable.Data>
                        </GridTable.Container>
                    </GridTable>
                </div>
            </MainContainer>
        </>
    );
};

DiscountSimulationPage.propTypes = {
    isDesktop: bool.isRequired,
};
export default DiscountSimulationPage;
